import { useState, useEffect, useCallback } from 'react'
import { Package, Calendar, CreditCard, Key, Copy, CheckCircle } from 'lucide-react'
import { useAuth } from '../../auth/hooks/useAuth'
import AuthModal from '../../auth/components/AuthModal'
import { apiGetJson } from '../../../lib/apiClient'
import { unwrapList } from '../../../lib/apiUnwrap'

interface GiftItem {
  variant_id: string
  variant_name?: string
  product_name?: string
  quantity: number
}

interface PurchaseDetail {
  id: string
  amount: number
  status: string
  created_at: string
  variant_name?: string
  product_name?: string
  duration_days?: number
  key_value?: string
  account_info?: string
  guide_url?: string
  combo_name?: string
  gift_items?: GiftItem[]
}

interface PurchasedPageProps {
  embedded?: boolean
}

export default function PurchasedPage({ embedded = false }: PurchasedPageProps) {
  const { user, session, isInitializing } = useAuth()
  const [purchases, setPurchases] = useState<PurchaseDetail[]>([])
  const [loading, setLoading] = useState(true)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const [copiedLink, setCopiedLink] = useState<string | null>(null)

  const fetchPurchases = useCallback(async () => {
    if (!user) return

    try {
      const resp = await apiGetJson<any>('/api/transactions?type=purchase&limit=200')
      const data = unwrapList<any>(resp)

      const formattedPurchases = (data || []).map((t: any) => ({
        id: t.id,
        amount: t.amount,
        status: t.status,
        created_at: t.created_at,
        variant_name: t.variant_name || t.product_variants?.name,
        product_name: t.product_name || t.product_variants?.products?.name,
        duration_days: t.duration_days ?? t.product_variants?.duration_days,
        key_value: t.key_value || t.product_keys?.key_value || (Array.isArray(t?.metadata?.keys) && t.metadata.keys.length ? String(t.metadata.keys[0]) : undefined),
        account_info: t.account_info || t.product_variants?.products?.account_info,
        guide_url: t.guide_url || t.product_variants?.guide_url || t.product_variants?.products?.guide_url || t?.metadata?.guide_url,
        combo_name: t?.metadata?.combo_name,
        gift_items: t?.metadata?.gift_items as GiftItem[] | undefined
      }))

      setPurchases(formattedPurchases)
    } catch (error) {
      console.error('Error fetching purchases:', error)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    if (isInitializing) return

    // Dùng session để check đăng nhập thay vì user profile
    if (session) {
      setShowAuthModal(false)
      if (user) {
        fetchPurchases()
      }
    } else {
      setShowAuthModal(true)
      setLoading(false)
    }
  }, [user, session, isInitializing, fetchPurchases])

  const copyToClipboard = (key: string, id: string) => {
    navigator.clipboard.writeText(key).then(() => {
      setCopiedKey(id)
      setTimeout(() => setCopiedKey(null), 2000)
    })
  }

  const copyLinkToClipboard = (link: string, id: string) => {
    navigator.clipboard.writeText(link).then(() => {
      setCopiedLink(id)
      setTimeout(() => setCopiedLink(null), 2000)
    })
  }

  const getStatusBadge = (status: string) => {
    const statusMap = {
      pending: { text: 'Chờ xử lý', bg: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' },
      processing: { text: 'Đang xử lý', bg: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200' },
      completed: { text: 'Thành công', bg: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200' },
      refunded: { text: 'Đã hoàn tiền', bg: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200' },
      failed: { text: 'Thất bại', bg: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200' },
    }
    const statusInfo = statusMap[status as keyof typeof statusMap] || statusMap.pending
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusInfo.bg}`}>
        {statusInfo.text}
      </span>
    )
  }

  if (!user) {
    if (embedded) {
      return (
        <div className="text-center py-10">
          <p className="text-gray-500">Vui lòng đăng nhập để xem lịch sử mua hàng</p>
        </div>
      )
    }

    return (
      <>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Đã Mua</h1>
            <p className="text-gray-600">Vui lòng đăng nhập để xem lịch sử mua hàng</p>
          </div>
        </div>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      </>
    )
  }

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'h-40' : 'h-64'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    )
  }

  return (
    <div className={embedded ? '' : 'max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
      {!embedded ? (
        <div className="flex items-center space-x-3 mb-8">
          <Package className="h-8 w-8 text-amber-600" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Đã Mua</h1>
        </div>
      ) : null}

      {purchases.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
          <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Chưa có đơn hàng nào</h3>
          <p className="text-gray-500">
            Bạn chưa mua sản phẩm nào. Hãy khám phá các sản phẩm tại cửa hàng!
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {purchases.map((purchase) => (
            <div key={purchase.id} className="bg-white dark:bg-gray-900 rounded-lg shadow-md border dark:border-gray-800 hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="bg-amber-100 dark:bg-amber-900/30 rounded-full p-2">
                      <CreditCard className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {purchase.product_name || 'Sản phẩm'}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {purchase.variant_name}
                        {(purchase.duration_days || 0) === 0
                          ? ' - Vĩnh viễn'
                          : ` - ${purchase.duration_days} ngày`}
                      </p>
                      <div className="flex items-center space-x-1 text-xs text-gray-500 mt-1">
                        <Calendar className="h-3 w-3" />
                        <span>
                          {new Date(purchase.created_at).toLocaleDateString('vi-VN', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                  {getStatusBadge(purchase.status)}
                </div>

                <div className="border-t dark:border-gray-800 pt-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-gray-600">Số tiền:</span>
                      <div className="text-xl font-bold text-red-600 dark:text-red-400">
                        -{Math.abs(purchase.amount).toLocaleString('vi-VN')}đ
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-sm text-gray-600">Mã GD:</span>
                      <div className="font-mono text-xs text-gray-500">
                        #{purchase.id.slice(-8).toUpperCase()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Combo Gift Info */}
                {purchase.combo_name && (
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800 mb-4">
                    <div className="flex items-start space-x-3">
                      <div className="bg-purple-100 dark:bg-purple-900/30 rounded-full p-2 mt-1">
                        <Package className="h-5 w-5 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-purple-900 dark:text-purple-200 mb-2">🎁 Combo quà tặng: {purchase.combo_name}</h4>
                        {purchase.gift_items && purchase.gift_items.length > 0 && (
                          <ul className="space-y-1">
                            {purchase.gift_items.map((gift, idx) => (
                              <li key={idx} className="text-sm text-purple-700 dark:text-purple-300 flex items-center">
                                <span className="mr-2">•</span>
                                <span>
                                  {gift.product_name ? `${gift.product_name} - ` : ''}{gift.variant_name || 'Sản phẩm'} x{gift.quantity}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {purchase.status === 'completed' && purchase.key_value && (
                  <div className="bg-gradient-to-r from-green-50 to-amber-50 dark:from-green-950/20 dark:to-amber-950/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                    <div className="flex items-start space-x-3">
                      <div className="bg-green-100 dark:bg-green-900/30 rounded-full p-2 mt-1">
                        <Key className="h-5 w-5 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Key sản phẩm của bạn:</h4>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between gap-2">
                            <code className="text-sm font-mono text-gray-900 dark:text-gray-100 select-all break-all min-w-0">
                              {purchase.key_value}
                            </code>
                            <button
                              onClick={() => copyToClipboard(purchase.key_value!, purchase.id)}
                              className="ml-1 flex items-center space-x-1 px-3 py-1.5 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors text-sm whitespace-nowrap flex-shrink-0"
                              title="Sao chép key"
                            >
                              {copiedKey === purchase.id ? (
                                <>
                                  <CheckCircle className="h-4 w-4" />
                                  <span>Đã sao chép!</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="h-4 w-4" />
                                  <span>Sao chép</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                          ⚠️ Chúng tôi sẽ không chịu trách nhiệm nếu key này không còn hiển thị trên web sau này.
                        </p>
                      </div>
                    </div>

                    {purchase.guide_url && (
                      <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-950/20 rounded-xl border border-amber-100 dark:border-amber-800">
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center space-x-3">
                            <div className="bg-amber-600 p-2 rounded-lg flex-shrink-0">
                              <Package className="h-4 w-4 text-white" />
                            </div>
                            <div className="min-w-0">
                              <span className="text-sm font-bold text-blue-900 dark:text-amber-200 block">Hướng dẫn cài đặt:</span>
                              <span className="text-xs text-amber-600 dark:text-amber-400">Vui lòng đọc kỹ hướng dẫn trước khi cài</span>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <a
                              href={purchase.guide_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-2 bg-white dark:bg-gray-800 text-amber-600 dark:text-amber-400 border border-blue-200 dark:border-gray-700 rounded-lg text-sm font-bold hover:bg-amber-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
                            >
                              <CheckCircle className="h-4 w-4 flex-shrink-0" />
                              <span>Xem online</span>
                            </a>
                            <button
                              onClick={() => copyLinkToClipboard(purchase.guide_url!, purchase.id)}
                              className={`px-3 py-2 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-1.5 shadow-sm ${copiedLink === purchase.id
                                ? 'bg-green-500 text-white border-green-500'
                                : 'bg-amber-600 text-white border-amber-600 hover:bg-amber-700'
                                }`}
                            >
                              {copiedLink === purchase.id ? (
                                <>
                                  <CheckCircle className="h-4 w-4 flex-shrink-0" />
                                  <span>Đã sao chép!</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="h-4 w-4 flex-shrink-0" />
                                  <span>Sao chép liên kết</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {purchase.status === 'completed' && !purchase.key_value && !purchase.account_info && (
                  <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                    <p className="text-sm text-green-700 dark:text-green-300 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Giao dịch đã hoàn tất thành công!
                    </p>
                  </div>
                )}

                {purchase.status === 'failed' && (
                  <div className="bg-red-50 dark:bg-red-950/20 rounded-lg p-3 border border-red-200 dark:border-red-800">
                    <p className="text-sm text-red-700 dark:text-red-300">
                      ❌ Giao dịch thất bại. Vui lòng liên hệ hỗ trợ.
                    </p>
                  </div>
                )}

                {(purchase.status === 'pending' || purchase.status === 'processing') && (
                  <div className="bg-yellow-50 dark:bg-yellow-950/20 rounded-lg p-3 border border-yellow-200 dark:border-yellow-800">
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      ⏳ Giao dịch đang được xử lý...
                    </p>
                  </div>
                )}

                {purchase.status === 'refunded' && (
                  <div className="bg-rose-50 dark:bg-rose-950/20 rounded-lg p-3 border border-rose-200 dark:border-rose-800">
                    <p className="text-sm text-rose-700 dark:text-rose-300">
                      💸 Đơn hàng đã được hoàn tiền về ví của bạn.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
